import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, first, switchMap, tap } from 'rxjs/operators';
import { ConfigurationService } from '../../../services/configuration.service';
import { AppUrls } from '../../../app-urls';
import { ConfigurationRoot } from 'src/app/shared/models/configuration-root';
import { MenuItem } from '../interfaces/menu-item.interface';
import { MenuFacade } from '../interfaces/menu-facade.interface';
import { UserMenuItemEnum } from '../enums/user-menu-item.enum';
import { LinkTypeEnum } from '../../../enums/link-type.enum';
import { UnprocessedClarification } from '../interfaces/unprocessed-clarification.interface';
import { MainMenuService } from '../api/main-menu.service';
import { CookieService } from 'ngx-cookie-service';
import { RegulationsAcceptResponse } from '../interfaces/regulations-accept-response.interface';

@Injectable()
export class CustomerMainMenuFacade implements MenuFacade {
  public menuItems$ = new BehaviorSubject<MenuItem[]>([]);
  public isRegulationsVisible$ = new BehaviorSubject<boolean>(false);

  private _configuration: ConfigurationRoot;
  private _menuItems: MenuItem[];
  private _selectedMenuItem: MenuItem | null = null;

  constructor(
    private _configurationService: ConfigurationService,
    private _mainMenuService: MainMenuService,
    private _cookieService: CookieService
  ) {
    this._configuration = this._configurationService.configuration;
  }

  public buildMenu(): Observable<number> {
    this._menuItems = [];
    return this._mainMenuService.getUserMenu().pipe(
      first((menuItems) => !!menuItems),
      tap((menuItems: MenuItem[]) => {
        this._initMenuItems(menuItems);
        this.menuItems$.next(this._menuItems);
      }),
      switchMap(() => this._setMenuCounterLabels())
    );
  }

  public hideRegulationsWindow(): void {
    this.isRegulationsVisible$.next(false);
  }

  public setRegulationsAccept(): Observable<RegulationsAcceptResponse> {
    return this._mainMenuService.setRegulationsAccept().pipe(
      first(),
      tap(() => {
        this._cookieService.set(
          'showAnalyticInfoReglamentWindow',
          'false',
          null,
          '/',
          '.rts-tender.ru',
          true
        );
        window.open(this._selectedMenuItem.url, '_blank');
      })
    );
  }

  private _initMenuItems(items: MenuItem[], isSubCategory = false): void {
    items.forEach((menuItem) => {
      switch (menuItem.id) {
        case UserMenuItemEnum.REGISTER_OF_INQUIRIES_CATEGORY:
          menuItem.countMarkLabel =
            'Количество неотвеченных актуальных запросов на разъяснение положений извещения / Количество неотвеченных актуальных запросов на разъяснение результатов';
          break;
        case UserMenuItemEnum.PROTOCOLS_CATEGORY:
          if (this._configuration.user.isCustomer) {
            menuItem.countMarkLabel =
              'Количество протоколов на публикацию / Количество протоколов на подпись';
          }
          break;
        case UserMenuItemEnum.CUSTOMER_STAR_SERVICES:
        case UserMenuItemEnum.OKPD2_CHECK:
        case UserMenuItemEnum.COMMERCIAL_PROPOSALS_REQUEST_SERVICE:
        case UserMenuItemEnum.NMCK_JUSTIFICATION_OF_PRODUCTS:
        case UserMenuItemEnum.NMCK_JUSTIFICATION_OF_MEDICAL_PRODUCTS:
        case UserMenuItemEnum.NMCK_JUSTIFICATION_OF_MEDICINES:
        case UserMenuItemEnum.NMCK_JUSTIFICATION_OF_SECURITY_SERVICES:
        case UserMenuItemEnum.CALCULATOR_OF_PURCHASE_TIME:
        case UserMenuItemEnum.DOCUMENTATION_EXAMPLES_SEARCH:
        case UserMenuItemEnum.DOCUMENTATION_CHECK:
        case UserMenuItemEnum.SUPPLIERS_SEARCH:
        case UserMenuItemEnum.AFFILIATION_CHECK:
        case UserMenuItemEnum.GOVERNMENT_PROCEDURES_PRICE_MONITORING:
        case UserMenuItemEnum.PROCUREMENT_QUOTAS:
        case UserMenuItemEnum.COUNTERPARTY_CHECK:
          menuItem.urlType = LinkTypeEnum.URL_NEW_TAB;
          menuItem.onClick = (event: MouseEvent) => {
            this._showAnalyticInfoRegulationsWindow(menuItem, event);
          };
          break;
      }

      if (menuItem.items?.length) {
        this._initMenuItems(menuItem.items, true);
      }

      if (!isSubCategory) {
        this._menuItems.push(menuItem);
      }
    });
  }

  private _setMenuCounterLabels() {
    return this._setRegisterOfInquiriesCounterLabel().pipe(
      switchMap(() => this._setCalendarCounterLabel()),
      switchMap(() => this._setFavoritesCounterLabel()),
      switchMap(() => this._setProtocolsCounterLabel()),
      finalize(() => this.menuItems$.next([...this._menuItems]))
    );
  }

  private _setRegisterOfInquiriesCounterLabel(): Observable<UnprocessedClarification> {
    let clarificationRequests: UnprocessedClarification;
    return this._mainMenuService
      .getUnprocessedClarificationRequests(this._configuration.links.tenderPlatformBaseUrl)
      .pipe(
        catchError(() => of(null)),
        tap((response: UnprocessedClarification) => (clarificationRequests = response)),
        switchMap(() =>
          this._mainMenuService.getUnprocessedResultClarification(
            this._configuration.links.tenderPlatformBaseUrl
          )
        ),
        catchError(() => of(null)),
        tap((resultClarification: UnprocessedClarification) => {
          if (clarificationRequests?.count || resultClarification?.count) {
            this._setLinkCountMark(
              UserMenuItemEnum.REGISTER_OF_INQUIRIES_CATEGORY,
              clarificationRequests?.count,
              resultClarification?.count,
              '0'
            );
            this._setLinkCountLabel(
              UserMenuItemEnum.REGISTER_OF_INQUIRIES_CATEGORY,
              clarificationRequests?.title,
              resultClarification?.title
            );
          }
        })
      );
  }

  private _setCalendarCounterLabel(): Observable<number> {
    return this._mainMenuService
      .getToDayEvents(this._configuration.links.tenderPlatformBaseUrl)
      .pipe(
        tap((calendarEventsCount) =>
          this._setLinkCountMark(UserMenuItemEnum.CALENDAR_CATEGORY, calendarEventsCount)
        ),
        catchError(() => of(0))
      );
  }

  private _setFavoritesCounterLabel(): Observable<number> {
    return this._mainMenuService.getFavoriteTradesCount(this._configuration.links.fcsBaseUrl).pipe(
      tap((favoritesCount) =>
        this._setLinkCountMark(UserMenuItemEnum.FAVORITE_TRADES_CATEGORY, favoritesCount)
      ),
      catchError(() => of(0))
    );
  }

  private _setProtocolsCounterLabel(): Observable<number> {
    const tenderPlatformBaseUrl = this._configuration.links.tenderPlatformBaseUrl;
    let publishCounter: number;
    return this._mainMenuService
      .getProtocolFilterCounter(`${tenderPlatformBaseUrl}${AppUrls.mainMenu.getFilterPublishCount}`)
      .pipe(
        tap((count) => (publishCounter = count)),
        catchError(() => of(0)),
        switchMap(() =>
          this._mainMenuService.getProtocolFilterCounter(
            `${tenderPlatformBaseUrl}${AppUrls.mainMenu.getFilterSigningCount}`
          )
        ),
        tap((count) => {
          if (this._configuration.user.isCustomer && (publishCounter || count)) {
            this._setLinkCountMark(UserMenuItemEnum.PROTOCOLS_CATEGORY, publishCounter, count);
          }
        }),
        catchError(() => of(0))
      );
  }

  private _setLinkCountMark(
    id: UserMenuItemEnum,
    primaryCount: number,
    secondaryCount?: number,
    defaultCountValue = ''
  ): void {
    const menuItem = this._menuItems.find((menuItem) => menuItem.id === id);
    if (menuItem) {
      const primaryLabel = this._convertToCounterLabel(primaryCount, defaultCountValue);
      const secondaryLabel = this._convertToCounterLabel(secondaryCount, defaultCountValue);

      menuItem.countMark =
        primaryLabel && secondaryLabel
          ? `${primaryLabel} / ${secondaryLabel}`
          : primaryLabel || secondaryLabel;
    }
  }

  private _setLinkCountLabel(
    id: UserMenuItemEnum,
    primaryLabel: string,
    secondaryLabel = ''
  ): void {
    const menuItem = this._menuItems.find((menuItem) => menuItem.id === id);
    if (menuItem) {
      menuItem.countMarkLabel =
        primaryLabel && secondaryLabel
          ? `${primaryLabel} / ${secondaryLabel}`
          : primaryLabel || secondaryLabel;
    }
  }

  private _showAnalyticInfoRegulationsWindow(menuItem: MenuItem, event: MouseEvent): void {
    if (JSON.parse(this._cookieService.get('showAnalyticInfoReglamentWindow') || 'true')) {
      event.preventDefault();
      this.isRegulationsVisible$.next(true);
      this._selectedMenuItem = menuItem;
    }
  }

  private _convertToCounterLabel(counter: number, defaultValue = ''): string {
    return counter >= 100 ? '99+' : counter ? counter.toString() : defaultValue;
  }
}
